import React, { useState, useMemo } from "react";
import { Box, Grid, Stack, Button, useTheme, Alert } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useNavigate } from "react-router-dom";

import {
  StatCard,
  StatCardSkeleton,
  DonutChart,
  HorizontalBarChart,
  ProgressBarChart,
  DemographicsChart,
  AgeDistributionChart,
} from "../../../../../shared/components/charts";
import {
  DashboardCard,
  DashboardCardSkeleton,
} from "../../../../../shared/components";
import {
  FilterDrawer,
  FilterState,
  getAppliedFilterCount,
  DurationSelector,
  DateRange,
} from "../../../../../shared/components";
import {
  useProgrammeProfileStats,
  useAgeDistribution,
  useWorkingEnvironment,
  useDemographics,
  useTravelTime,
  useProgrammeGoals,
  useWorkingHourPatterns,
  useActiveUsersByRole,
} from "../hooks/useProgrammeProfile";
import { buildFilterParams } from "../../shared/utils";
import { transformProgrammeGoals } from "../utils/transformData";
import { getStatCardIcon } from "../../../../../shared/utils";
import type { MetricStatValue } from "../../../../../shared/api/types";
import { useClientScope } from "../../../../../shared/contexts";
import {
  usePersistedDateRange,
  usePersistedFilters,
} from "../../../../../shared/hooks";
import { ROUTES } from "../../../../../shared/utils";

export const ProgrammeProfile: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { effectiveClientId } = useClientScope();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { filters, setFilters } = usePersistedFilters();
  const activeFiltersCount = getAppliedFilterCount(filters);
  const { dateRange, setDateRange } = usePersistedDateRange();

  const filterParams = useMemo(
    () => buildFilterParams(filters, dateRange, effectiveClientId),
    [filters, dateRange, effectiveClientId]
  );

  const {
    data: statsData,
    isLoading: statsLoading,
    error: statsError,
  } = useProgrammeProfileStats(filterParams);

  const {
    data: ageDistributionData,
    isLoading: ageDistributionLoading,
    error: ageDistributionError,
  } = useAgeDistribution(filterParams);

  const {
    data: workingEnvironmentData,
    isLoading: workingEnvironmentLoading,
    error: workingEnvironmentError,
  } = useWorkingEnvironment(filterParams);

  const {
    data: demographicsData,
    isLoading: demographicsLoading,
    error: demographicsError,
  } = useDemographics(filterParams);

  const {
    data: travelTimeData,
    isLoading: travelTimeLoading,
    error: travelTimeError,
  } = useTravelTime(filterParams);

  const {
    data: programmeGoalsData,
    isLoading: programmeGoalsLoading,
    error: programmeGoalsError,
  } = useProgrammeGoals(filterParams);

  const {
    data: workingHourPatternsData,
    isLoading: workingHourPatternsLoading,
    error: workingHourPatternsError,
  } = useWorkingHourPatterns(filterParams);

  const {
    data: activeUsersByRoleData,
    isLoading: activeUsersByRoleLoading,
    error: activeUsersByRoleError,
  } = useActiveUsersByRole(filterParams);

  const handleApplyFilters = (newFilters: FilterState) => {
    setFilters(newFilters);
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
  };

  const statCards = useMemo(() => {
    if (!statsData?.data) return [];
    const metricKeys = [
      "total_users",
      "active_users",
      "participation_rate",
      "completion_rate",
    ];
    return metricKeys
      .filter((key) => statsData.data[key])
      .map((key) => ({
        key,
        metric: statsData.data[key],
        icon: getStatCardIcon(key),
      }));
  }, [statsData]);

  const openParticipantsForMetric = (metricKey: string) => {
    const params = new URLSearchParams();
    if (metricKey === "active_users") {
      params.set("status", "Active");
    }
    if (metricKey === "completion_rate") {
      params.set("status", "Completed");
    }
    navigate(
      `${ROUTES.PARTICIPANTS}${params.toString() ? `?${params.toString()}` : ""}`
    );
  };

  const transformedProgrammeGoals = useMemo(
    () => transformProgrammeGoals(programmeGoalsData),
    [programmeGoalsData]
  );

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <DurationSelector
              value={dateRange}
              onChange={handleDateRangeChange}
              buttonVariant="outlined"
            />
            <Button
              variant="text"
              size="medium"
              startIcon={<FilterListIcon />}
              color={activeFiltersCount > 0 ? "primary" : "inherit"}
              onClick={() => setIsFilterOpen(true)}
            >
              {activeFiltersCount > 0 ? `FILTER (${activeFiltersCount})` : "FILTER"}
            </Button>
          </Box>
        </Box>

        <FilterDrawer
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={handleApplyFilters}
          initialFilters={filters}
        />

        {statsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load statistics. Please try again.
          </Alert>
        )}

        <Grid container spacing={3}>
          {statsLoading
            ? Array.from({ length: 4 }).map((_, index) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                  <StatCardSkeleton />
                </Grid>
              ))
            : statCards.map((stat, index) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                  <StatCard
                    metric={stat.metric}
                    icon={stat.icon}
                    onClick={() => openParticipantsForMetric(stat.key)}
                  />
                </Grid>
              ))}
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            {ageDistributionLoading ? (
              <DashboardCardSkeleton title subtitle height={220} />
            ) : ageDistributionError ? (
              <DashboardCard
                title="Age Distribution"
                subtitle="Organisation Demographic"
              >
                <Alert severity="error">
                  Failed to load age distribution data.
                </Alert>
              </DashboardCard>
            ) : (
              <DashboardCard
                title="Age Distribution"
                subtitle="Organisation Demographic"
              >
                <AgeDistributionChart
                  data={
                    ageDistributionData?.data?.distribution.map((item) => ({
                      label: item.label,
                      value: item.value,
                    })) || []
                  }
                  height={220}
                />
              </DashboardCard>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            {workingEnvironmentLoading ? (
              <DashboardCardSkeleton title subtitle height={220} />
            ) : workingEnvironmentError ? (
              <DashboardCard
                title="Working environment"
                subtitle="Percentage of employees based in various environment"
              >
                <Alert severity="error">
                  Failed to load working environment data.
                </Alert>
              </DashboardCard>
            ) : (
              <DashboardCard
                title="Working environment"
                subtitle="Percentage of employees based in various environment"
              >
                <DonutChart
                  data={
                    workingEnvironmentData?.data?.distribution.map((item) => ({
                      label: item.label,
                      value: item.value,
                    })) || []
                  }
                  height={220}
                />
              </DashboardCard>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            {demographicsLoading ? (
              <DashboardCardSkeleton title subtitle height={220} />
            ) : demographicsError ? (
              <DashboardCard
                title="Employee Demographics Overview"
                subtitle="Breakdown of participants by gender"
              >
                <Alert severity="error">
                  Failed to load demographics data.
                </Alert>
              </DashboardCard>
            ) : (
              <DashboardCard
                title="Employee Demographics Overview"
                subtitle="Breakdown of participants by gender"
              >
                <DemographicsChart
                  data={demographicsData?.data?.distribution || []}
                />
              </DashboardCard>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            {travelTimeLoading ? (
              <DashboardCardSkeleton title subtitle height={220} />
            ) : travelTimeError ? (
              <DashboardCard
                title="Travel Time"
                subtitle="Frequency of travel commitments across the workforce"
              >
                <Alert severity="error">Failed to load travel time data.</Alert>
              </DashboardCard>
            ) : (
              <DashboardCard
                title="Travel Time"
                subtitle="Frequency of travel commitments across the workforce"
              >
                <DonutChart
                  data={
                    travelTimeData?.data?.distribution.map((item) => ({
                      label: item.label,
                      value: item.value,
                    })) || []
                  }
                  height={220}
                />
              </DashboardCard>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            {programmeGoalsLoading ? (
              <DashboardCardSkeleton title subtitle height={220} />
            ) : programmeGoalsError ? (
              <DashboardCard
                title="Programme Goals"
                subtitle="Understanding what employees want to achieve"
              >
                <Alert severity="error">
                  Failed to load programme goals data.
                </Alert>
              </DashboardCard>
            ) : (
              <DashboardCard
                title="Programme Goals"
                subtitle="Understanding what employees want to achieve"
              >
                <ProgressBarChart data={transformedProgrammeGoals} />
              </DashboardCard>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            {workingHourPatternsLoading ? (
              <DashboardCardSkeleton title subtitle height={220} />
            ) : workingHourPatternsError ? (
              <DashboardCard
                title="Working Hour Patterns"
                subtitle="Typical work schedule distribution"
              >
                <Alert severity="error">
                  Failed to load working hour patterns data.
                </Alert>
              </DashboardCard>
            ) : (
              <DashboardCard
                title="Working Hour Patterns"
                subtitle="Typical work schedule distribution"
              >
                <DonutChart
                  data={
                    workingHourPatternsData?.data?.distribution.map((item) => ({
                      label: item.label,
                      value: item.value,
                    })) || []
                  }
                  height={220}
                />
              </DashboardCard>
            )}
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 12 }}>
            {activeUsersByRoleLoading ? (
              <DashboardCardSkeleton title subtitle height={380} />
            ) : activeUsersByRoleError ? (
              <DashboardCard
                title="Active users by Role"
                subtitle="Participation levels segmented by job role"
              >
                <Alert severity="error">
                  Failed to load active users by role data.
                </Alert>
              </DashboardCard>
            ) : (
              <DashboardCard
                title="Active users by Role"
                subtitle="Participation levels segmented by job role"
              >
                <HorizontalBarChart
                  data={
                    activeUsersByRoleData?.data?.distribution.map((item) => ({
                      label: item.label,
                      value: item.value,
                    })) || []
                  }
                  height={380}
                />
              </DashboardCard>
            )}
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
